import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { authedLayoutRoute } from './authed.js';
import { listEmployees } from '../api/employees.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatDate } from '../lib/format.js';
import type { EmployeeResponse } from '@kora/types';

/**
 * /employees — paginated list of all employees in the current org.
 *
 * TanStack Query handles:
 *   - caching (30s default staleTime from main.tsx)
 *   - automatic refetch on window focus after stale
 *   - deduplication if two components mount the same query
 *
 * Pagination: keyset via `cursor` — we only track the current cursor in
 * local state for Day 17 (one page at a time). Infinite scroll / prev
 * button lands when real orgs approach 50+ employees.
 */

export const employeesRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/employees',
  component: EmployeesListPage,
});

type PayrollModelFilter = 'all' | 'local_employee' | 'international_remote';

function EmployeesListPage(): JSX.Element {
  const navigate = useNavigate();
  const [payrollFilter, setPayrollFilter] = useState<PayrollModelFilter>('all');
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['employees', 'list'],
    queryFn: () => listEmployees({ limit: 50 }),
  });

  // ADR 0010 Phase 4 — client-side filter (the list endpoint has no
  // payrollModel query param; the loaded page is filtered in place).
  const visible =
    data && payrollFilter !== 'all'
      ? data.employees.filter((e) => e.payrollModel === payrollFilter)
      : (data?.employees ?? []);

  return (
    <section className="p-8 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl">Employees</h1>
          <p className="text-sm text-gray-400">
            Everyone on payroll. Add new hires, edit profiles, terminate.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            aria-label="Filter by payroll model"
            value={payrollFilter}
            onChange={(e) => setPayrollFilter(e.target.value as PayrollModelFilter)}
            className="rounded border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white focus:border-white focus:outline-none"
          >
            <option value="all">All payroll models</option>
            <option value="local_employee">Local employee</option>
            <option value="international_remote">International remote</option>
          </select>
          <button
            type="button"
            onClick={() => void navigate({ to: '/employees/import' })}
            className="rounded border border-gray-700 text-gray-200 px-4 py-2 text-sm font-medium hover:bg-gray-900"
          >
            Bulk import (CSV)
          </button>
          <button
            type="button"
            onClick={() => void navigate({ to: '/employees/new' })}
            className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200"
          >
            Add employee
          </button>
        </div>
      </header>

      {isLoading ? <LoadingState /> : null}

      {isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : null}

      {data && data.employees.length === 0 ? <EmptyState /> : null}

      {data && data.employees.length > 0 ? (
        <EmployeeTable employees={visible} />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function EmployeeTable({ employees }: { employees: EmployeeResponse[] }): JSX.Element {
  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-left text-gray-400 uppercase text-xs">
          <tr>
            <th className="px-4 py-3 font-medium">Employee #</th>
            <th className="px-4 py-3 font-medium">Name</th>
            <th className="px-4 py-3 font-medium">Department</th>
            <th className="px-4 py-3 font-medium">Position</th>
            <th className="px-4 py-3 font-medium">Country</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Hired</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((e) => (
            <tr
              key={e.id}
              className="border-t border-gray-900 hover:bg-gray-950"
            >
              <td className="px-4 py-3 font-mono text-xs">
                <Link
                  to="/employees/$id"
                  params={{ id: e.id }}
                  className="hover:underline"
                >
                  {e.employeeNumber}
                </Link>
              </td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-2">
                  <Link
                    to="/employees/$id"
                    params={{ id: e.id }}
                    className="hover:underline"
                  >
                    {e.firstName} {e.lastName}
                  </Link>
                  {/* ADR 0010 — badge for international_remote only (local = no badge). */}
                  {e.payrollModel === 'international_remote' ? (
                    <span className="text-xs px-2 py-0.5 rounded border bg-blue-950 text-blue-300 border-blue-900">
                      International remote
                    </span>
                  ) : null}
                </span>
              </td>
              <td className="px-4 py-3 text-gray-400">{e.department ?? '—'}</td>
              <td className="px-4 py-3 text-gray-400">{e.position ?? '—'}</td>
              <td className="px-4 py-3 text-gray-400">
                {e.countryCode}
                {e.stateCode ? ` · ${e.stateCode}` : ''}
              </td>
              <td className="px-4 py-3">
                <StatusPill status={e.status} />
              </td>
              <td className="px-4 py-3 text-gray-400">{formatDate(e.hireDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }: { status: EmployeeResponse['status'] }): JSX.Element {
  const map: Record<EmployeeResponse['status'], string> = {
    active: 'bg-green-950 text-green-400 border-green-900',
    on_leave: 'bg-yellow-950 text-yellow-400 border-yellow-900',
    terminated: 'bg-gray-900 text-gray-500 border-gray-800',
  };
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded border ${map[status]}`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Non-happy states
// ---------------------------------------------------------------------------

function LoadingState(): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
      Loading employees...
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="rounded border border-dashed border-gray-800 p-10 text-center space-y-3">
      <h2 className="font-medium text-white">No employees yet</h2>
      <p className="text-sm text-gray-400">
        Add your first hire to start running payroll.
      </p>
      <Link
        to="/employees/new"
        className="inline-block rounded bg-white text-black px-4 py-2 text-sm font-medium"
      >
        Add employee
      </Link>
    </div>
  );
}

function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}): JSX.Element {
  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : 'Unknown error';
  return (
    <Alert
      variant="error"
      title="Could not load employees"
      action={
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-red-800 px-3 py-1 text-xs hover:bg-red-900/40"
        >
          Retry
        </button>
      }
    >
      {message}
    </Alert>
  );
}
